import { useState } from "react";
import { updateMember, type Member, type PositionType } from "../../services/companies-api";

/** FR-049: the three org levels, in the order the product owner listed them. */
const POSITION_TYPES: { value: PositionType; label: string }[] = [
  { value: "employee", label: "Employee" },
  { value: "manager", label: "Manager" },
  { value: "bod", label: "BOD" },
];

/** FR-032/FR-038/FR-039/FR-049: edit each member's department, Position Type, System Manager
 * flag, Takes Test toggle, and password directly in the roster table. */
export function CompanyMembersTable({
  companyId,
  members,
  onChanged,
}: {
  companyId: string;
  members: Member[];
  onChanged: () => void;
}) {
  const [editingPasswordFor, setEditingPasswordFor] = useState<string | null>(null);
  const [newPassword, setNewPassword] = useState("");
  const [saving, setSaving] = useState<string | null>(null);

  async function handleDepartmentChange(member: Member, department: string) {
    setSaving(member.id);
    try {
      await updateMember(companyId, member.id, { department: department || null });
      onChanged();
    } finally {
      setSaving(null);
    }
  }

  async function handlePositionTypeChange(member: Member, positionType: PositionType) {
    setSaving(member.id);
    try {
      await updateMember(companyId, member.id, { positionType });
      onChanged();
    } finally {
      setSaving(null);
    }
  }

  async function handleToggle(member: Member, field: "isManager" | "takesTest", value: boolean) {
    setSaving(member.id);
    try {
      await updateMember(companyId, member.id, { [field]: value });
      onChanged();
    } finally {
      setSaving(null);
    }
  }

  async function handlePasswordSave(member: Member) {
    if (!newPassword) return;
    setSaving(member.id);
    try {
      await updateMember(companyId, member.id, { password: newPassword });
      setEditingPasswordFor(null);
      setNewPassword("");
      onChanged();
    } finally {
      setSaving(null);
    }
  }

  return (
    <table className="w-full text-left text-sm">
      <thead>
        <tr className="border-b border-surface-alt text-body">
          <th className="py-2">Name</th>
          <th>Email</th>
          <th>Department</th>
          <th>Position Type</th>
          <th>System Manager</th>
          <th>Takes Test</th>
          <th>Password</th>
        </tr>
      </thead>
      <tbody>
        {members.map((m) => (
          <tr key={m.id} className="border-b border-surface-alt">
            <td className="py-2 text-ink">{m.fullName}</td>
            <td>{m.email}</td>
            <td>
              <input
                defaultValue={m.department ?? ""}
                placeholder="Unassigned"
                onBlur={(e) => {
                  if (e.target.value !== (m.department ?? "")) handleDepartmentChange(m, e.target.value);
                }}
                className="w-32 rounded border border-surface-alt px-2 py-1"
                disabled={saving === m.id}
              />
            </td>
            <td>
              <select
                aria-label={`Position type for ${m.fullName}`}
                value={m.positionType}
                onChange={(e) => handlePositionTypeChange(m, e.target.value as PositionType)}
                className="rounded border border-surface-alt px-2 py-1"
                disabled={saving === m.id}
              >
                {POSITION_TYPES.map((p) => (
                  <option key={p.value} value={p.value}>
                    {p.label}
                  </option>
                ))}
              </select>
            </td>
            <td>
              <input
                type="checkbox"
                aria-label={`${m.fullName} is a System Manager`}
                checked={m.isManager}
                onChange={(e) => handleToggle(m, "isManager", e.target.checked)}
                disabled={saving === m.id}
              />
            </td>
            <td>
              <input
                type="checkbox"
                aria-label={`${m.fullName} takes the test`}
                checked={m.takesTest}
                onChange={(e) => handleToggle(m, "takesTest", e.target.checked)}
                disabled={saving === m.id || !m.isManager}
                title={!m.isManager ? "Always true for non-managers" : undefined}
              />
            </td>
            <td>
              {editingPasswordFor === m.id ? (
                <div className="flex gap-1">
                  <input
                    type="text"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    className="w-28 rounded border border-surface-alt px-2 py-1"
                    aria-label={`New password for ${m.fullName}`}
                  />
                  <button onClick={() => handlePasswordSave(m)} className="text-sm text-brand">
                    Save
                  </button>
                </div>
              ) : (
                <button
                  onClick={() => {
                    setEditingPasswordFor(m.id);
                    setNewPassword("");
                  }}
                  className="text-sm text-brand"
                >
                  Change
                </button>
              )}
            </td>
          </tr>
        ))}
        {members.length === 0 && (
          <tr>
            <td colSpan={7} className="py-6 text-center text-body">
              No members yet — upload a roster CSV above.
            </td>
          </tr>
        )}
      </tbody>
    </table>
  );
}
